import { useContext, useState, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { handlePageNumberInPagination } from 'common/pagination';
import { paginationDropdownOptions } from 'constants/dropdownOptions';
import { UserDetailsContext } from 'context/userDetailsContext';
import { getCaseAutomationInstanceActions, skipCaseActionInstance } from 'services/case/caseServices';
import { getActionDisplayText } from 'modules/automation/utils/getActionDisplayText';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';

export function useAutomationList(caseId, _pageSize = 10) {
  const userContext = useContext(UserDetailsContext);

  const [data, setData] = useState([]);
  const [isForward, setIsForward] = useState(true);
  const [pageSize, setPageSize] = useState(userContext?.firmDetails?.pagination_preference?.case_automation?.self ?? _pageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track current pagination state to maintain page after operations
  const [currentPaginationToken, setCurrentPaginationToken] = useState('');
  const [currentPaginationDirection, setCurrentPaginationDirection] = useState('forward');

  const next_Token = data?.next_token;
  const transition_Token = data?.transition_token;
  const automationList = data?.automation_instance || [];

  const processedAutomationList = useMemo(() => {
    const userTimezone = userContext?.userDetails?.timezone;

    return automationList.map((item) => {
      return {
        ...item,
        month: convertDateFromApiToUserTimezone(item.scheduled_time, userTimezone, 'MMM YYYY')?.toUpperCase() || '',
        automation: item.execute_link?.action_label || 'Unknown Action',
        description: getActionDisplayText(item?.execute_link),
        errors: item.error_message || '',
        date: convertDateFromApiToUserTimezone(item.scheduled_time, userTimezone, 'MMM D, YYYY'),
        time: convertDateFromApiToUserTimezone(item.scheduled_time, userTimezone, 'h:mm A'),
        executedDate: item.executed_time ? convertDateFromApiToUserTimezone(item.executed_time, userTimezone, 'MMM D, YYYY') : '',
        executedTime: item.executed_time ? convertDateFromApiToUserTimezone(item.executed_time, userTimezone, 'h:mm A') : '',
        skipped_at: item.skipped_at ? convertDateFromApiToUserTimezone(item.skipped_at, userTimezone, 'MMM D, YYYY h:mm A') : '',
        module: item.execute_link?.module || '',
      };
    });
  }, [automationList, userContext?.userDetails?.timezone]);

  useEffect(() => {
    setPageSize(userContext?.firmDetails?.pagination_preference?.case_automation?.self);
  }, [userContext]);

  const hasNext = next_Token === null && isForward;
  const hasPrevious = transition_Token === null || (transition_Token && next_Token === null && !isForward);

  const fetchNext = () => {
    setIsForward(true);
    loadAutomationList({
      limit: pageSize,
      pagination_token: !isForward ? transition_Token : next_Token,
      pagination_direction: 'forward',
    });
  };

  const fetchPrev = () => {
    setIsForward(false);
    loadAutomationList({
      limit: pageSize,
      pagination_token: isForward ? transition_Token : next_Token,
      pagination_direction: 'reverse',
    });
  };

  const onPageChange = (e) => {
    setPageSize(e.value);
    handlePageNumberInPagination(userContext, 'case_automation', '', 'self', e.value);
    loadAutomationList({
      limit: e.value,
      pagination_token: '',
      pagination_direction: 'forward',
    });
    setIsForward(true);
  };

  const loadAutomationList = ({ limit = pageSize, pagination_token = '', pagination_direction = 'forward' }) => {
    if (!caseId) {
      console.error('Case ID is required to load automation list');
      return;
    }

    setLoading(true);
    setError(null);

    // Track the pagination state for maintaining page after operations
    setCurrentPaginationToken(pagination_token);
    setCurrentPaginationDirection(pagination_direction);

    getCaseAutomationInstanceActions({
      case_id: caseId,
      limit,
      pagination_token,
      pagination_direction,
    })
      .then((response) => {
        setData(response?.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error loading automation list:', error);
        setError(error);
        setLoading(false);
      });
  };

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={onPageChange} />
        </>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchNext} disabled={hasNext} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchPrev} disabled={hasPrevious} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
  };

  const skipAction = async (pk, sk) => {
    try {
      setLoading(true);
      const response = await skipCaseActionInstance({ pk, sk });

      loadAutomationList({
        limit: pageSize,
        pagination_token: currentPaginationToken,
        pagination_direction: currentPaginationDirection,
      });

      return response;
    } catch (error) {
      console.error('Error skipping action:', error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      loadAutomationList({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  return {
    automationList: processedAutomationList,
    loadAutomationList,
    skipAction,
    loading,
    error,
    paginatorTemplate,
    pageSize,
  };
}
